import type { InvoiceFileByIdResponse } from './types';

import {
  type QueryFunctionContext,
  queryOptions,
  type UseSuspenseQueryOptions,
  useSuspenseQuery,
} from '@tanstack/react-query';
import ms from 'ms';

import type { UUID } from '~/types/common';

import { queryKeysInvoice } from '../queryKeys';

import { fetchInvoiceFileById } from './fetchInvoiceFileById';

type InvoiceFileByIdQueryOptions = Omit<
  UseSuspenseQueryOptions<InvoiceFileByIdResponse>,
  'queryFn' | 'queryKey'
>;

/**
 * Builds query options for one invoice document selected by file-row ID.
 *
 * @param invoiceId - Invoice identifier.
 * @param fileId - File-row identifier.
 * @param options - Additional suspense-query options.
 * @returns Query options for downloading the document.
 */
// fallow-ignore-next-line unused-export -- canonical query API for imperative consumers.
export function getInvoiceFileByIdQueryOptions({
  fileId,
  invoiceId,
  options,
}: {
  fileId: UUID;
  invoiceId: UUID;
  options?: InvoiceFileByIdQueryOptions;
}) {
  return queryOptions({
    gcTime: ms('10m'),
    queryFn: ({ signal }: QueryFunctionContext) =>
      fetchInvoiceFileById({ fileId, invoiceId }, { signal }),
    queryKey: queryKeysInvoice.getFileById(invoiceId, fileId),
    staleTime: Infinity,
    ...options,
  });
}

/**
 * Downloads one invoice document selected by file-row ID.
 *
 * @param invoiceId - Invoice identifier.
 * @param fileId - File-row identifier.
 * @param options - Additional suspense-query options.
 * @returns Suspense query result containing the document Blob.
 * @see https://app.dev.vestigas.com/redoc#tag/Invoices/operation/get_invoice_file_by_id_invoices__invoice_id__file__file_id__get
 */
export function useQueryInvoiceFileById(
  invoiceId: UUID,
  fileId: UUID,
  options?: InvoiceFileByIdQueryOptions,
) {
  return useSuspenseQuery(
    getInvoiceFileByIdQueryOptions({ fileId, invoiceId, options }),
  );
}
